import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { headData, headDataWidget } from "./paymentActivityData";
import { createFilterProps, typeMapper } from "utility";

import {
  EXTENDED_VIEW_WIDTH,
  MOBILE_WIDTH,
  PAYMENT_ACTIVITY_WIDET_COUNT,
} from "constants/common";
import { Filters } from "./filters/Filters";
import {
  FtTable,
  SortType,
  FtTableHeadData,
} from "components/common/table/FtTable";
import LoadTranslation from "components/common/LoadTranslation";

import { useRootDispatch, useRootSelector } from "store/hooks";
import { useRequestDispatch } from "store/requestHooks";

import { FilterProps } from "store/common";
import { useScreenWidth } from "hooks/useScreenWidth";
import { PaymentActivity as PaymentActivityDTO } from "store/paymentActivity/paymentActivity-state";
import {
  FetchPayments,
  fetchPayments,
} from "store/paymentActivity/paymentActivity-action";
import {
  clearFilter,
  selectAllPaymentActivity,
  setFilters,
} from "store/paymentActivity/paymentActivity-slice";
import { selectedAccountInfo } from "store/account/account-selector";

import classes from "./paymentActivity.module.scss";

interface PaymentActivityProps {
  hideControls?: boolean;
  showAllButton?: boolean;
  widgetView?: boolean;
  hideHeader?: boolean;
}

export const PaymentActivity: React.FC<PaymentActivityProps> = ({
  hideControls,
  showAllButton,
  widgetView,
  hideHeader,
}) => {
  const { t } = useTranslation(["common", "payment"]);

  const [openDialog, setOpenDialog] = useState(false);

  const dispatch = useRootDispatch();
  const requestDispatch = useRequestDispatch();
  const navigate = useNavigate();
  const width = useScreenWidth();

  const accountInfo = useRootSelector(selectedAccountInfo);
  const { filters, meta, serviceMeta } = useRootSelector(
    (state) => state.paymentActility,
  );
  const { client } = useRootSelector((state) => state.config);

  const expandedView = width <= EXTENDED_VIEW_WIDTH;
  const mobileView = width <= MOBILE_WIDTH;

  let records: PaymentActivityDTO[] = [];

  useEffect(() => {
    resetFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountInfo]);

  records = useRootSelector(selectAllPaymentActivity);

  const filterClickHandler = () => {
    setOpenDialog(true);
  };

  const wrapperClass = widgetView
    ? [classes.Wrapper]
    : [classes.Wrapper, classes.SelfContainer];

  const getTableItem = (data: PaymentActivityDTO[]) => {
    return widgetView ? data.slice(0, PAYMENT_ACTIVITY_WIDET_COUNT) : data;
  };

  const getLabelTranslation = (id: string) => {
    return t(`payment:${id}`);
  };

  const getHeadObject = (head: FtTableHeadData[]) => {
    const headItems = widgetView ? headDataWidget : head;
    const transformedHeadItem = headItems.map((single: FtTableHeadData) => {
      return {
        ...single,
        label: single.label ? single.label : getLabelTranslation(single.id),
      };
    });
    return transformedHeadItem;
  };

  let totalRowCount = serviceMeta?.totalCount;
  if (totalRowCount && records && records.length > totalRowCount) {
    totalRowCount = 0;
  }

  const chipRemoveHandler = (key: string) => {
    const filtersTemp = { ...filters };

    // here we use key "startDate" and "endDate" they both use as date-range
    // and need to remove as once one found.
    // else all other key have there on filter chips
    if (key === "startDate" || key === "endDate") {
      delete filtersTemp.minDate;
      delete filtersTemp.maxDate;
    } else {
      const value = null;
      filtersTemp[key as keyof typeof filtersTemp] = value as any;
    }

    const filterDTO = createFilterProps(filtersTemp);

    if (filterDTO) {
      dispatch(setFilters(filtersTemp));
      requestDispatch<FetchPayments, FilterProps>(fetchPayments, filterDTO);
    } else {
      resetFilter();
    }
  };

  const resetFilter = () => {
    const paymentFilter: FilterProps = {
      accountNumber: accountInfo?.accountNumber,
    };
    dispatch(clearFilter());
    dispatch(setFilters(paymentFilter));
    requestDispatch<FetchPayments, FilterProps>(fetchPayments, paymentFilter);
  };

  const chipContent = (
    value: string,
    key: string,
    allowRemove: boolean = true,
  ) => (
    <div key={key} className={classes.Chip}>
      <div className={classes.ChipText}>{value}</div>
      {allowRemove && (
        <ft-icon onClick={() => chipRemoveHandler(key)} class={classes.Pointer}>
          close
        </ft-icon>
      )}
    </div>
  );

  const renderChips = () => {
    const chipsContent: Array<any> = [];
    for (let filter in filters) {
      const val = filters[filter as keyof typeof filters];
      if (val) {
        if (filter === "startDate") {
          chipsContent.push(chipContent("Date Range", filter));
        } else if (filter === "endDate") {
          if (!filters.minDate)
            chipsContent.push(chipContent("Date Range", filter));
        } else if (filter === "status") {
          chipsContent.push(
            chipContent(
              typeMapper(
                parseInt(val as string),
                "PaymentStatusReverse",
              ) as string,
              filter,
            ),
          );
        } else if (filter === "paymentMethodType") {
          chipsContent.push(
            chipContent(
              t(
                `common:${
                  typeMapper(
                    parseInt(val as string),
                    "paymentMethodTypeReverse",
                  ) as string
                }_label`,
              ),
              filter,
            ),
          );
        } else if (filter !== "accountNumber") {
          chipsContent.push(chipContent(val as string, filter));
        }
      }
    }
    if (chipsContent.length > 0) {
      chipsContent.unshift(
        <div
          key="all"
          onClick={resetFilter}
          className={[classes.Chip, classes.Pointer].join(" ")}
        >
          <div className={classes.ChipText}>Clear All</div>
        </div>,
      );
    }
    return chipsContent;
  };

  return (
    <>
      <section className={wrapperClass.join(" ")}>
        {openDialog ? (
          <Filters filters={filters} setOpenDialog={setOpenDialog}></Filters>
        ) : null}
        {hideHeader ? null : (
          <>
            <div className={classes.Header}>
              <div className={classes.HeaderButtonWrapper}></div>
              <div className={classes.HeaderTitle}>
                {t("common:payment_activity")}
              </div>
              <div className={classes.Filter} onClick={filterClickHandler}>
                {hideControls ? null : (
                  <>
                    <ft-button
                      class={["outline-button", classes.WebFilterButton].join(
                        " ",
                      )}
                      label={t("payment:filters_label")}
                      outlined
                      icon={"filter_list"}
                      trailingIcon
                    ></ft-button>
                    <ft-icon class={classes.MobileFIlterButton}>
                      filter_list
                    </ft-icon>
                  </>
                )}
              </div>
            </div>
            <div className={classes.Chips}>{renderChips()}</div>
          </>
        )}
        {headData ? (
          <div className={classes.TableWraper}>
            <FtTable
              tableHeight={
                widgetView
                  ? ""
                  : mobileView
                    ? "calc(var(--appHeight) - 290px)"
                    : "calc(var(--appHeight) - 375px)"
              }
              dataAction={fetchPayments}
              headData={getHeadObject(widgetView ? headDataWidget : headData)}
              tableData={getTableItem(records ? records : [])}
              sortColumn={
                typeMapper(
                  serviceMeta?.sortColumn ? serviceMeta.sortColumn : "",
                  "SortColumnReverse",
                ) as string
              }
              sortDir={
                typeMapper(
                  serviceMeta?.sortDir ? serviceMeta.sortDir : "",
                  "SortDirReverse",
                ) as SortType
              }
              pageNum={serviceMeta?.pageNum}
              autoHeight={widgetView ? true : false}
              fillHeight={!widgetView ? true : false}
              loading={meta?.isLoading}
              pagination={widgetView ? false : true}
              totalCount={totalRowCount}
              filters={filters}
              createFilterProps={createFilterProps}
              rowExpandedHeight={widgetView ? 135 : 195}
              expandedView={expandedView}
              showHeader={!expandedView}
              shortPagination={mobileView ? true : false}
              emptyMessage={t("common:no_data_found_payment_activity")}
            />
            {showAllButton && records && records.length > 0 ? (
              <div className={classes.ButtonWrapper}>
                <ft-button
                  label={t("common:view_all_payment")}
                  unelevated
                  onClick={() => {
                    navigate(`/${client.customerKey}/dashboard/payments`);
                  }}
                  data-testid="payment-activity-view-all-button"
                ></ft-button>
              </div>
            ) : null}
          </div>
        ) : null}
        <LoadTranslation keys={["receipt"]} />
        {!widgetView && <LoadTranslation keys={["payment_final"]} />}
      </section>
    </>
  );
};
